import {
  arrayUnion,
  doc,
  increment,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import db from '../firebase/firebaseConfig';

const SHORTLINK_METRICS_COLLECTION = 'shortlinkMetrics';
const SHORTLINK_VISITORS_COLLECTION = 'shortlinkVisitors';
const VISITOR_GEO_CACHE_KEY = 'hcva_shortlink_geo';
const VISITOR_SESSION_ID_KEY = 'hcva_visitor_sid';

function getVisitorSessionId() {
  try {
    let id = sessionStorage.getItem(VISITOR_SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem(VISITOR_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

/** sessionStorage: set before navigating to shortlink — consumed once on embed load. */
export const SHORTLINK_VISIT_NOTE_KEY = 'hcva_shortlink_visit_note';
export const SHORTLINK_VISIT_FROM_POPUP = 'popup';
export const SHORTLINK_VISIT_FROM_REDIRECT_GATE = 'redirect_gate';

/** Human-readable note for why geo/device row was recorded (embed vs fullscreen). */
export function consumePendingVisitNoteForEmbed() {
  try {
    const v = sessionStorage.getItem(SHORTLINK_VISIT_NOTE_KEY);
    sessionStorage.removeItem(SHORTLINK_VISIT_NOTE_KEY);
    if (v === SHORTLINK_VISIT_FROM_POPUP) {
      return 'Fetched due to popup screen visit.';
    }
    if (v === SHORTLINK_VISIT_FROM_REDIRECT_GATE) {
      return 'Fetched due to redirect gate — click opened shortlink embed.';
    }
  } catch {
    /* ignore */
  }
  return 'Fetched due to shortlink embed load.';
}

export const VISIT_NOTE_FULLSCREEN =
  'Fetched due to fullscreen triggered.';

function getDeviceType() {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|iemobile|blackberry|opera mini/.test(ua)) return 'mobile';
  return 'desktop';
}

/** Human-readable OS for admin (macOS / Windows / Linux / iOS / Android / Other). */
export function parseOsLabel(ua, platform) {
  const u = (ua || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(u)) return 'iOS';
  if (/android/.test(u)) return 'Android';
  if (/macintosh|mac os x/.test(u)) return 'macOS';
  if (/windows/.test(u)) return 'Windows';
  if (/linux/.test(u)) return 'Linux';
  const p = (platform || '').toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('win')) return 'Windows';
  return 'Other';
}

/** Browser name from user-agent string. */
export function parseBrowserName(ua) {
  const u = ua || '';
  if (/Edg\//i.test(u)) return 'Edge';
  if (/OPR\/|Opera\/|Opera Mini/i.test(u)) return 'Opera';
  if (/Firefox\//i.test(u)) return 'Firefox';
  if (/Chrome\/|CriOS/i.test(u)) return 'Chrome';
  if (/Safari/i.test(u) && !/Chrome/i.test(u)) return 'Safari';
  return 'Other';
}

/**
 * IP + country (HTTPS, browser-safe). Cached per session.
 * Primary: ipapi.co; fallback: ipify + optional ipwho.is lookup.
 */
async function resolveVisitorGeo() {
  try {
    const raw = sessionStorage.getItem(VISITOR_GEO_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ip === 'string') return parsed;
    }
  } catch {
    /* ignore */
  }

  const empty = { ip: '', countryName: '', countryCode: '' };

  try {
    const res = await fetch('https://ipapi.co/json/');
    if (res.ok) {
      const d = await res.json();
      if (!d || d.error) throw new Error(d?.reason || 'ipapi error');
      const ip = typeof d.ip === 'string' ? d.ip : '';
      const countryName =
        typeof d.country_name === 'string'
          ? d.country_name
          : typeof d.country === 'string' && d.country.length > 2
            ? d.country
            : '';
      let countryCode =
        typeof d.country_code === 'string'
          ? d.country_code
          : typeof d.country === 'string' && d.country.length === 2
            ? d.country
            : '';
      countryCode = (countryCode || '').toUpperCase().slice(0, 2);
      const out = { ip, countryName: countryName || '', countryCode };
      if (out.ip) {
        try {
          sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify(out));
        } catch {
          /* ignore */
        }
        return out;
      }
    }
  } catch {
    /* try fallbacks */
  }

  try {
    const res = await fetch('https://api.ipify.org?format=json');
    if (!res.ok) return empty;
    const data = await res.json();
    const ip = typeof data?.ip === 'string' ? data.ip : '';
    if (!ip) return empty;

    let countryName = '';
    let countryCode = '';
    try {
      const g = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`);
      if (g.ok) {
        const w = await g.json();
        if (w.success !== false && w.country) {
          countryName = typeof w.country === 'string' ? w.country : '';
          countryCode =
            typeof w.country_code === 'string'
              ? w.country_code.toUpperCase().slice(0, 2)
              : '';
        }
      }
    } catch {
      /* country unknown */
    }

    const out = { ip, countryName, countryCode };
    try {
      sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify(out));
    } catch {
      /* ignore */
    }
    return out;
  } catch {
    return empty;
  }
}

export async function trackShortlinkFullscreenTrigger({ siteId, slug, sourceDomain }) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  try {
    await setDoc(
      doc(db, SHORTLINK_METRICS_COLLECTION, normalizedSiteId),
      {
        siteId: normalizedSiteId,
        fullscreenTriggers: increment(1),
        lastSlug: slug || '',
        lastSourceDomain: sourceDomain || '',
        lastFullscreenAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (err) {
    console.error('[shortlinkMetrics] fullscreen trigger write failed:', err);
  }
}

export async function trackShortlinkVisitor({
  siteId,
  slug,
  sourceDomain,
  visitNote = ''
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  const geo = await resolveVisitorGeo();
  const ua = navigator.userAgent || '';
  const language = navigator.language || '';
  const platform = navigator.platform || '';
  const osLabel = parseOsLabel(ua, platform);
  const browserName = parseBrowserName(ua);
  const visitorSessionId = getVisitorSessionId();
  const sessionRef = doc(
    db,
    SHORTLINK_VISITORS_COLLECTION,
    normalizedSiteId,
    'sessions',
    visitorSessionId
  );

  const iso = new Date().toISOString();
  const logLine = `${iso}|${visitNote || '—'}`;

  const firstDocKey = `hcva_shortlink_fs_first_${normalizedSiteId}_${visitorSessionId}`;
  let isFirstWriteToSessionDoc = false;
  try {
    isFirstWriteToSessionDoc = !sessionStorage.getItem(firstDocKey);
  } catch {
    isFirstWriteToSessionDoc = true;
  }

  const payload = {
    visitorSessionId,
    siteId: normalizedSiteId,
    slug: slug || '',
    lastSlug: slug || '',
    sourceDomain: sourceDomain || '',
    ipAddress: geo.ip || '',
    countryName: geo.countryName || '',
    countryCode: geo.countryCode || '',
    deviceType: getDeviceType(),
    osLabel,
    browserName,
    userAgent: ua,
    language,
    platform,
    lastVisitNote: visitNote || '',
    lastVisitedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    visitLog: arrayUnion(logLine),
    visitCount: increment(1)
  };

  if (isFirstWriteToSessionDoc) {
    payload.firstVisitedAt = serverTimestamp();
  }

  try {
    await setDoc(sessionRef, payload, { merge: true });
    try {
      sessionStorage.setItem(firstDocKey, '1');
    } catch {
      /* ignore */
    }
  } catch (err) {
    console.error('[shortlinkMetrics] visitor session write failed:', err);
  }
}
